import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Loading from 'components/Loading';
import Toaster from 'components/Toaster';
import GetUser from 'pages/app/Users/gql/queries/get-user';
import UpdateUserCapabilities from './gql/UpdateUserCapabilities';

// Capability keys (excluding account_id)
const capabilityKeys = [
  'events',
  'guests',
  'add_guests',
  'check_in_guests',
  'messages',
  'add_promos',
  'promos',
  'credits',
  'issue_credits',
  'transactions',
  'issue_refunds',
  'registrations',
  'customers',
  'memberships',
  'staff',
  'add_staff',
  'stripe',
  'scan_tickets',
  'check_tickets',
  'create_messages',
  'edit_registrations',
  'edit_memberships',
  'edit_billing',
  'pos',
  'cancel_event',
  'registration_email',
  'annual_email',
  'membership_email',
  'refund_request_email',
];

// Capability groups for organized display
const capabilityGroups = [
  {
    title: 'Events',
    description: 'Control access to event management features',
    keys: ['events', 'scan_tickets', 'check_tickets', 'cancel_event'],
    labels: {
      events: 'View Events',
      scan_tickets: 'Scan Passes/Tickets',
      check_tickets: 'Manually Check-in',
      cancel_event: 'Cancel/Postpone Events',
    },
  },
  {
    title: 'Discounts',
    description: 'Manage promotional codes and discounts',
    keys: ['promos', 'add_promos'],
    labels: {
      promos: 'View Promo Codes',
      add_promos: 'Issue Discounts',
    },
  },
  {
    title: 'Staff',
    description: 'Manage staff member access',
    keys: ['staff', 'add_staff'],
    labels: {
      staff: 'View Staff',
      add_staff: 'Add Staff',
    },
  },
  {
    title: 'Guest Lists',
    description: 'Control guest list management',
    keys: ['guests', 'add_guests', 'check_in_guests'],
    labels: {
      guests: 'View Guest Lists',
      add_guests: 'Create Guest List',
      check_in_guests: 'Check In Guests',
    },
  },
  {
    title: 'Credits',
    description: 'Manage customer credits',
    keys: ['credits', 'issue_credits'],
    labels: {
      credits: 'View Credits',
      issue_credits: 'Issue Credits',
    },
  },
  {
    title: 'Transactions',
    description: 'View and manage customer transactions',
    keys: ['transactions', 'issue_refunds'],
    labels: {
      transactions: 'View Transactions',
      issue_refunds: 'Issue Refunds',
    },
  },
  {
    title: 'Messages',
    description: 'Access to messaging features',
    keys: ['messages', 'create_messages'],
    labels: {
      messages: 'View Messages',
      create_messages: 'Send Messages',
    },
  },
  {
    title: 'Other',
    description: 'Additional account permissions',
    keys: ['stripe', 'customers', 'edit_billing', 'pos'],
    labels: {
      stripe: 'Stripe Access',
      customers: 'View Customers',
      edit_billing: 'Edit Billing',
      pos: 'Use POS',
    },
  },
  {
    title: 'Registrations',
    description: 'Customer registration management',
    keys: ['registrations', 'edit_registrations'],
    labels: {
      registrations: 'View Registrations',
      edit_registrations: 'Edit Registrations',
    },
    hideForTicketHoss: true,
  },
  {
    title: 'Memberships',
    description: 'Membership management access',
    keys: ['memberships', 'edit_memberships'],
    labels: {
      memberships: 'View Memberships',
      edit_memberships: 'Edit Memberships',
    },
    hideForTicketHoss: true,
  },
  {
    title: 'Email Notifications',
    description: 'Control which email notifications this user receives',
    keys: [
      'registration_email',
      'annual_email',
      'membership_email',
      'refund_request_email',
    ],
    labels: {
      registration_email: 'Registration Emails',
      annual_email: 'Annual Emails',
      membership_email: 'Membership Emails',
      refund_request_email: 'Refund Request Emails',
    },
  },
];

// Page styling
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

const Container = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 16px;

  &:hover {
    color: #fa4616;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0'};
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-color: ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1'};
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, #fa4616, #ff6b3b);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const MemberInfoCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MemberAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fa4616;
  font-size: 20px;
  font-weight: 600;
`;

const MemberDetails = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 4px;
`;

const MemberContact = styled.div`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`;

// Full Access Card with Checkmarks
const FullAccessV4 = styled.div`
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
  border-radius: 16px;
  padding: 28px;
`;

const FullAccessV4Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
`;

const FullAccessV4Badge = styled.div`
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.15)'
      : 'rgba(34, 197, 94, 0.1)'};
  color: ${({ theme }) => theme.colors.success};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const FullAccessV4Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

const FullAccessV4Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FullAccessV4Item = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.1)'
      : 'rgba(34, 197, 94, 0.05)'};
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#e2e8f0' : '#374151')};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.success};
    flex-shrink: 0;
  }
`;

const AccessLevelCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const AccessLevelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const AccessLevelOptions = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const AccessOption = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: ${({ selected, theme }) =>
    selected
      ? theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.08)'
      : theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : '#f8fafc'};
  border: 2px solid ${({ selected }) => (selected ? '#fa4616' : 'transparent')};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.1)'
        : 'rgba(250, 70, 22, 0.05)'};
  }
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  accent-color: #fa4616;
  cursor: pointer;
`;

const OptionContent = styled.div``;

const OptionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 4px;
`;

const OptionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
`;

const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CapabilityGroup = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  overflow: hidden;
`;

const GroupHeader = styled.div`
  padding: 16px 20px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)'};
`;

const GroupTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const GroupTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

const AllAccessToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #fa4616;
`;

const GroupDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  margin: 0;
`;

const GroupContent = styled.div`
  padding: 8px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9'};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#e2e8f0' : '#374151')};
`;

const MiniToggle = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  cursor: pointer;
  flex-shrink: 0;
`;

const MiniToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;

  &:checked + span {
    background-color: #fa4616;
  }

  &:checked + span:before {
    transform: translateX(18px);
  }
`;

const MiniToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.15)'};
  transition: all 0.3s ease;
  border-radius: 11px;

  &:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px;
    left: 3px;
    top: 3px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

// Icons
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const getInitials = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  return '?';
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(
      7
    )}`;
  }
  return phone;
};

const EditCapabilitiesContent = ({ data, updateUserCapabilities }) => {
  const { accountId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [capabilities, setCapabilities] = useState({});
  const [accessLevel, setAccessLevel] = useState('limited');
  const [isSaving, setIsSaving] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  const user = data?.getUser;
  const isLoading = data?.loading;

  // Determine route prefix for navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Initialize capabilities when user data loads
  useEffect(() => {
    if (user) {
      const memberCapabilities = user.capabilities?.find(
        (cap) => parseInt(cap.account_id) === parseInt(accountId)
      );

      if (memberCapabilities) {
        const caps = capabilityKeys.reduce((acc, key) => {
          acc[key] = memberCapabilities[key];
          return acc;
        }, {});
        setCapabilities(caps);
        const allTrue = capabilityKeys.every((key) => caps[key] === true);
        setAccessLevel(allTrue ? 'full' : 'limited');
      } else {
        const defaultCaps = capabilityKeys.reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {});
        setCapabilities(defaultCaps);
        setAccessLevel('limited');
      }
    }
  }, [user, accountId]);

  const handleAccessLevelChange = (level) => {
    setAccessLevel(level);
    if (level === 'full') {
      const allTrue = capabilityKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setCapabilities(allTrue);
    }
  };

  const handleCapabilityChange = (key, value) => {
    setCapabilities((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleGroupAllAccess = (groupKeys, value) => {
    setCapabilities((prev) => {
      const updated = { ...prev };
      groupKeys.forEach((key) => {
        updated[key] = value;
      });
      return updated;
    });
  };

  const isGroupAllChecked = (groupKeys) => {
    return groupKeys.every((key) => capabilities[key] === true);
  };

  const handleBack = () => {
    history.push(`${routePrefix}/accounts/editAccount/${accountId}?tab=staff`);
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const response = await updateUserCapabilities({
        user_id: user.id,
        capabilities: [
          {
            ...capabilities,
            account_id: parseInt(accountId),
          },
        ],
      });

      if (!response || response.errors) {
        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to update permissions. Please try again.',
        });
      } else {
        // Redirect with success state to show toaster on main page
        history.push({
          pathname: `${routePrefix}/accounts/editAccount/${accountId}`,
          search: '?tab=staff',
          state: {
            toaster: {
              type: 'success',
              title: 'Success!',
              message: 'Permissions updated successfully.',
            },
          },
        });
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to update permissions. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingContainer>
            <Loading size={50} />
          </LoadingContainer>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <BackButton type="button" onClick={handleBack}>
          <ArrowLeftIcon />
          Back to Account
        </BackButton>

        <PageHeader>
          <HeaderLeft>
            <Title>Edit Permissions</Title>
            <Subtitle>
              Configure what this staff member can access and manage
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <CancelButton type="button" onClick={handleBack}>
              Cancel
            </CancelButton>
            <SaveButton type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Permissions'}
            </SaveButton>
          </HeaderRight>
        </PageHeader>

        <MemberInfoCard>
          <MemberAvatar>
            {getInitials(user?.first_name, user?.last_name)}
          </MemberAvatar>
          <MemberDetails>
            <MemberName>
              {user?.first_name} {user?.last_name}
            </MemberName>
            <MemberContact>
              {user?.cellphone && (
                <ContactItem>
                  <PhoneIcon />
                  {formatPhoneNumber(user.cellphone)}
                </ContactItem>
              )}
              {user?.email && (
                <ContactItem>
                  <EmailIcon />
                  {user.email}
                </ContactItem>
              )}
            </MemberContact>
          </MemberDetails>
        </MemberInfoCard>

        <AccessLevelCard>
          <AccessLevelTitle>Access Level</AccessLevelTitle>
          <AccessLevelOptions>
            <AccessOption selected={accessLevel === 'full'}>
              <RadioInput
                type="radio"
                name="accessLevel"
                value="full"
                checked={accessLevel === 'full'}
                onChange={() => handleAccessLevelChange('full')}
              />
              <OptionContent>
                <OptionTitle>Full Access</OptionTitle>
                <OptionDescription>
                  Grant access to all features and permissions
                </OptionDescription>
              </OptionContent>
            </AccessOption>
            <AccessOption selected={accessLevel === 'limited'}>
              <RadioInput
                type="radio"
                name="accessLevel"
                value="limited"
                checked={accessLevel === 'limited'}
                onChange={() => handleAccessLevelChange('limited')}
              />
              <OptionContent>
                <OptionTitle>Limited Access</OptionTitle>
                <OptionDescription>
                  Choose specific permissions for this user
                </OptionDescription>
              </OptionContent>
            </AccessOption>
          </AccessLevelOptions>
        </AccessLevelCard>

        {accessLevel === 'full' ? (
          <FullAccessV4>
            <FullAccessV4Header>
              <FullAccessV4Badge>Full Access</FullAccessV4Badge>
              <FullAccessV4Title>All Permissions Enabled</FullAccessV4Title>
            </FullAccessV4Header>
            <FullAccessV4Grid>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Events
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Transactions
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Staff
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Messages
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Refunds
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Credits
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Guest Lists
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />
                Promo Codes
              </FullAccessV4Item>
              <FullAccessV4Item>
                <CheckCircleIcon />+ All Others
              </FullAccessV4Item>
            </FullAccessV4Grid>
          </FullAccessV4>
        ) : (
          <CapabilitiesGrid>
            {capabilityGroups
              .filter((group) => !(isTicketHoss && group.hideForTicketHoss))
              .map((group) => (
                <CapabilityGroup key={group.title}>
                  <GroupHeader>
                    <GroupTitleRow>
                      <GroupTitle>{group.title}</GroupTitle>
                      <AllAccessToggle>
                        <span>All</span>
                        <MiniToggle>
                          <MiniToggleInput
                            type="checkbox"
                            checked={isGroupAllChecked(group.keys)}
                            onChange={(e) =>
                              handleGroupAllAccess(group.keys, e.target.checked)
                            }
                          />
                          <MiniToggleSlider />
                        </MiniToggle>
                      </AllAccessToggle>
                    </GroupTitleRow>
                    <GroupDescription>{group.description}</GroupDescription>
                  </GroupHeader>
                  <GroupContent>
                    {group.keys.map((key) => (
                      <ToggleRow key={key}>
                        <ToggleLabel>{group.labels[key]}</ToggleLabel>
                        <MiniToggle>
                          <MiniToggleInput
                            type="checkbox"
                            checked={capabilities[key] || false}
                            onChange={(e) =>
                              handleCapabilityChange(key, e.target.checked)
                            }
                          />
                          <MiniToggleSlider />
                        </MiniToggle>
                      </ToggleRow>
                    ))}
                  </GroupContent>
                </CapabilityGroup>
              ))}
          </CapabilitiesGrid>
        )}
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

EditCapabilitiesContent.propTypes = {
  data: PropTypes.object,
  updateUserCapabilities: PropTypes.func.isRequired,
};

// Main component wrapped with theme provider and data
const EditCapabilities = (props) => {
  return (
    <EnhancedThemeProvider>
      <EditCapabilitiesContent {...props} />
    </EnhancedThemeProvider>
  );
};

export default compose(GetUser, UpdateUserCapabilities)(EditCapabilities);
